"use client";
import * as React from "react";
import { useState, useEffect } from "react";
import type {
  ConnectionData,
  ConnectionListProps,
  OIDCSSORecord,
  SAMLSSORecord,
} from "../types";
import LoadingContainer from "../../../shared/LoadingContainer/index";
import {
  BadgeProps,
  PageToken,
  PaginatePayload,
  TableProps,
} from "../../../shared/types";
import { sendHTTPRequest } from "../../../shared/http";
import Paginate from "../../../shared/Paginate/index";
import PaginatedTable from "../../../shared/Table/paginated";
import NonPaginatedTable from "../../../shared/Table/non-paginated";
const DEFAULT_VALUES = {
  isSettingsView: false,
  connectionListData: [] as ConnectionData<any>[],
};

function ConnectionList(props: ConnectionListProps) {
  const [connectionListData, setConnectionListData] = useState(
    () => DEFAULT_VALUES.connectionListData
  );

  const [isConnectionListLoading, setIsConnectionListLoading] = useState(
    () => true
  );

  const [pageTokenMap, setPageTokenMap] = useState(() => ({}));

  const [showErrorComponent, setShowErrorComponent] = useState(() => false);

  const [errorMessage, setErrorMessage] = useState(() => "");

  function getUrl() {
    return props.urls.get;
  }

  function isPaginated() {
    return props.paginate?.itemsPerPage !== undefined;
  }

  function colsToDisplay() {
    return (
      props.cols || [
        "name",
        "provider",
        "tenant",
        "product",
        "type",
        "status",
        "actions",
      ]
    ).map((_col) => {
      if (_col === "status") {
        return {
          name: "status",
          badge: {
            position: "surround",
            variantSelector(rowData) {
              let _variant: BadgeProps["variant"];
              if (rowData.deactivated) {
                _variant = "warning";
              }
              if (!rowData.deactivated) {
                _variant = "success";
              }
              return _variant;
            },
          },
        };
      } else if (_col === "name") {
        return {
          name: "name",
          badge: {
            position: "right",
            badgeText: "System",
            variant: "info",
            shouldDisplayBadge(rowData) {
              return rowData.isSystemSSO;
            },
          },
        };
      } else {
        return _col;
      }
    }) as TableProps["cols"];
  }

  function connectionProviderName(connection: SAMLSSORecord | OIDCSSORecord) {
    if ("idpMetadata" in connection) {
      return (
        connection.idpMetadata.friendlyProviderName ||
        connection.idpMetadata.provider
      );
    }
    if ("oidcProvider" in connection) {
      return (
        connection.oidcProvider.friendlyProviderName ||
        connection.oidcProvider.provider
      );
    }
    return "Unknown";
  }

  function actions() {
    return [
      {
        icon: "PencilIcon",
        label: "Edit",
        handleClick: (connection: ConnectionData<any>) =>
          props.handleActionClick("edit", connection),
      },
    ];
  }

  function listFetchUrl(
    params: Partial<PaginatePayload> &
      Pick<ConnectionListProps, "tenant" | "product" | "displaySorted"> & {
        getUrl: string;
      }
  ) {
    let _url = params.getUrl;
    const [urlPath, qs] = _url.split("?");
    const urlParams = new URLSearchParams(qs);
    if (params.tenant) {
      if (Array.isArray(params.tenant)) {
        for (const _tenant of params.tenant) {
          urlParams.append("tenant", _tenant);
        }
      } else {
        urlParams.set("tenant", params.tenant);
      }
    }
    if (params.product) {
      urlParams.set("product", params.product);
    }
    if (params.pageToken) {
      urlParams.set("pageToken", params.pageToken);
    }
    if (params.displaySorted) {
      urlParams.set("sort", "true");
    }
    if (params?.offset !== undefined) {
      urlParams.set("pageOffset", `${params.offset}`);
      urlParams.set("pageLimit", `${params.limit}`);
    }
    if (urlParams.toString()) {
      return `${urlPath}?${urlParams}`;
    }
    return _url;
  }

  function baseFetchUrl() {
    return listFetchUrl({
      getUrl: getUrl(),
      tenant: props.tenant,
      product: props.product,
      displaySorted: props.displaySorted,
    });
  }

  function updateTokenMap(offset: number, token: PageToken) {
    return {
      ...pageTokenMap,
      [offset]: token,
    };
  }

  async function getFieldsData(url: string) {
    setIsConnectionListLoading(true);
    type ConnectionList = ConnectionData<SAMLSSORecord | OIDCSSORecord>[];
    const response = await sendHTTPRequest<
      | ConnectionList
      | {
          data: ConnectionList;
          pageToken: PageToken;
        }
    >(url);
    setIsConnectionListLoading(false);
    if (response && typeof response === "object") {
      if ("error" in response && response.error) {
        setShowErrorComponent(true);
        setErrorMessage(response.error.message);
        typeof props.errorCallback === "function" &&
          props.errorCallback(response.error.message);
      } else {
        const isTokenizedPagination =
          typeof response === "object" && "pageToken" in response;
        const _data = isTokenizedPagination ? response.data : response;
        if (Array.isArray(_data)) {
          const _connectionsListData = _data.map(
            (connection: ConnectionData<any>) => {
              return {
                ...connection,
                provider: connectionProviderName(connection),
                type: "oidcProvider" in connection ? "OIDC" : "SAML",
                status: connection.deactivated ? "Inactive" : "Active",
                isSystemSSO: connection.isSystemSSO,
              };
            }
          );
          setConnectionListData(_connectionsListData);
          typeof props.handleListFetchComplete === "function" &&
            props.handleListFetchComplete(_connectionsListData);
        }
        if (isTokenizedPagination) {
          return response.pageToken;
        }
      }
    }
  }

  async function reFetch(payload: PaginatePayload) {
    const pageToken = await getFieldsData(
      listFetchUrl({
        getUrl: baseFetchUrl(),
        ...payload,
      })
    );
    if (pageToken) {
      setPageTokenMap(updateTokenMap(payload.offset, pageToken));
    }
  }

  useEffect(() => {
    if (!isPaginated()) {
      getFieldsData(baseFetchUrl());
    }
  }, [baseFetchUrl(), isPaginated()]);

  return (
    <LoadingContainer isBusy={isConnectionListLoading}>
      {isPaginated() ? (
        <Paginate
          itemsPerPage={props.paginate!.itemsPerPage}
          currentPageItemsCount={connectionListData.length}
          handlePageChange={props.paginate?.handlePageChange}
          reFetch={reFetch}
          pageTokenMap={pageTokenMap}
        >
          <PaginatedTable
            emptyStateMessage="No connections found."
            cols={colsToDisplay()}
            data={connectionListData}
            actions={actions()}
            showErrorComponent={showErrorComponent}
            errorMessage={errorMessage}
            tableProps={props.tableProps}
          />
        </Paginate>
      ) : null}
      {!isPaginated() ? (
        <NonPaginatedTable
          emptyStateMessage="No connections found."
          cols={colsToDisplay()}
          data={connectionListData}
          actions={actions()}
          showErrorComponent={showErrorComponent}
          errorMessage={errorMessage}
          tableProps={props.tableProps}
        />
      ) : null}
    </LoadingContainer>
  );
}

export default ConnectionList;
